import { useEffect, useState } from "react";

type PerformanceMetrics = {
  navigationTime: number;
  isOnline: boolean;
  connectionType?: string;
};

type ExtendedNavigator = Navigator & {
  connection?: {
    effectiveType?: string;
  };
  mozConnection?: {
    effectiveType?: string;
  };
  webkitConnection?: {
    effectiveType?: string;
  };
};

export const usePerformanceMonitoring = () => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    navigationTime: 0,
    isOnline: navigator.onLine,
  });

  useEffect(() => {
    // Monitor navigation timing
    const measureNavigationTime = () => {
      if (window.performance && window.performance.timing) {
        const navigationTime =
          window.performance.timing.loadEventEnd -
          window.performance.timing.navigationStart;

        setMetrics((prev) => ({ ...prev, navigationTime }));
      }
    };

    // Monitor online/offline status
    const handleOnline = () =>
      setMetrics((prev) => ({ ...prev, isOnline: true }));
    const handleOffline = () =>
      setMetrics((prev) => ({ ...prev, isOnline: false }));

    // Monitor connection type (if available)
    const updateConnectionType = () => {
      const extendedNavigator = navigator as ExtendedNavigator;
      const connection =
        extendedNavigator.connection ||
        extendedNavigator.mozConnection ||
        extendedNavigator.webkitConnection;
      if (connection) {
        setMetrics((prev) => ({
          ...prev,
          connectionType: connection.effectiveType,
        }));
      }
    };

    // Set up event listeners
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("load", measureNavigationTime);

    // Initial measurements
    measureNavigationTime();
    updateConnectionType();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("load", measureNavigationTime);
    };
  }, []);

  return metrics;
};
